import { useState } from "react";
import { FiCheck } from "react-icons/fi";
import { useSubmitPost } from "@/features/post/hooks/useSubmitPost";
import { useSettingsStore } from "@/features/settings/hooks";
import { acquireTurnstileToken } from "@/features/turnstile/lib/acquireToken";
import { isIOS } from "@/features/turnstile/lib/isIOS";
import { useTurnstileStore } from "@/features/turnstile/stores/turnstileStore";
import { useClipboardPaste } from "@/shared/hooks";
import { getFingerprint } from "@/shared/lib";
import {
  Button,
  Checkbox,
  ImageUpload,
  Input,
  Textarea,
} from "@/shared/ui/form";
import { PostNotice } from "@/shared/ui/navigation";

/**
 * スレッド保持期間 (時間 / 最小分) の上限・下限。新APIには
 * /v1/thread-limits 相当のエンドポイントが無いため、PHP backend の最下層
 * フォールバック値 (spec §3.1) を viewer 側で固定値として保持する。
 * 将来 API でボード単位の上限が公開されたら useThreadLimits を再導入する。
 */
interface ThreadLimits {
  durationHours: number;
  minMinutes: number;
}

const DEFAULT_THREAD_LIMITS: ThreadLimits = {
  durationHours: 12,
  minMinutes: 30,
};

interface ThreadCreateFormData {
  act: string;
  comment: string;
  // null = not user-touched → display seeded value from resolvedLimits
  duration: { hours: string; minutes: string } | null;
  allowImageReplies: boolean;
}

interface Props {
  onSuccess?: () => void;
}

function formatMinLabel(minMinutes: number): string {
  const m = Math.max(1, minMinutes);
  if (m < 60) return `${m}分`;
  const hours = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${hours}時間${rest}分` : `${hours}時間`;
}

function formatHint(limits: ThreadLimits): string {
  const hours = Math.max(1, limits.durationHours);
  return `(時:分 / ${formatMinLabel(limits.minMinutes)}〜${hours}時間)`;
}

function buildInitialDuration(limits: ThreadLimits): {
  hours: string;
  minutes: string;
} {
  // 既定値は上限の半分（分単位で算出）。上限ぎりぎりで立てると長居しすぎる
  // 可能性があるので穏当な値として total / 2 を採用。durationHours = 1 の
  // ケースでも 0:30 のように正しく半分を表現できるよう分単位で扱う。
  // minMinutes 未満にならないよう下限でクランプする。
  const halfMinutes = Math.floor((limits.durationHours * 60) / 2);
  const clamped = Math.max(halfMinutes, limits.minMinutes);
  return {
    hours: String(Math.floor(clamped / 60)),
    minutes: String(clamped % 60),
  };
}

function validateDuration(
  hours: string,
  minutes: string,
  limits: ThreadLimits,
): string | null {
  if (hours === "" || minutes === "") {
    return "保持期間を入力してください";
  }
  const h = Number(hours);
  const m = Number(minutes);
  if (!Number.isInteger(h) || !Number.isInteger(m)) {
    return "整数で入力してください";
  }
  if (m >= 60) {
    return "分は 0〜59 で入力してください";
  }
  if (h < 0 || m < 0) {
    return "0 以上で入力してください";
  }
  const total = h * 60 + m;
  if (total < limits.minMinutes) {
    return `${formatMinLabel(limits.minMinutes)}以上にしてください`;
  }
  if (total > limits.durationHours * 60) {
    return `${Math.max(1, limits.durationHours)}時間以下にしてください`;
  }
  return null;
}

export const ThreadCreateForm: React.FunctionComponent<Props> = ({
  onSuccess,
}: Props) => {
  const { deleteKey } = useSettingsStore();

  // 新APIには /v1/thread-limits 相当が無いため、固定のフォールバック値を採用。
  // 真の上限はサーバー側で再判定され、超過時は API エラーとして返る。
  const resolvedLimits: ThreadLimits = DEFAULT_THREAD_LIMITS;

  const [formData, setFormData] = useState<ThreadCreateFormData>({
    act: "",
    comment: "",
    duration: null,
    allowImageReplies: true,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // duration === null means the user hasn't typed yet; seed from resolvedLimits.
  // Once the user edits either field, `duration` becomes an object and
  // we no longer overwrite it.
  const effectiveDuration: { hours: string; minutes: string } =
    formData.duration ?? buildInitialDuration(resolvedLimits);

  const durationError: string | null = validateDuration(
    effectiveDuration.hours,
    effectiveDuration.minutes,
    resolvedLimits,
  );

  const { mutateAsync: submitPost, isPending } = useSubmitPost();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDurationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const base = prev.duration ?? buildInitialDuration(resolvedLimits);
      return {
        ...prev,
        duration: { ...base, [name]: value },
      };
    });
  };

  const handleFileChange = (file: File | null): void => {
    setSelectedFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  // クリップボードから画像を貼り付ける
  useClipboardPaste({
    onImagePaste: handleFileChange,
  });

  const handleSubmit = async (e: React.SubmitEvent): Promise<void> => {
    e.preventDefault();

    // ① 画像が必須
    if (!selectedFile) {
      alert("画像を選択してください。");
      return;
    }

    // ②-pre duration の範囲検証（バックエンド到達前）
    if (durationError) {
      alert(durationError);
      return;
    }

    // submit 前に focus を外して IME 入力を確定させる
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Turnstile token を取得
    let token: string;
    try {
      token = await acquireTurnstileToken();
    } catch (err) {
      alert(
        err instanceof Error
          ? err.message
          : "Turnstile トークン取得に失敗しました",
      );
      return;
    }

    // ② FormData組み立て
    const data = new FormData();
    data.append("email", formData.act);
    data.append("comment", formData.comment);
    data.append("password", deleteKey);
    data.append("allow_image_replies", formData.allowImageReplies ? "1" : "0");

    // ③ duration を H:M 文字列に組み立てて送信（バリデーション通過済みなので数値変換は安全）
    const hoursNum = Number(effectiveDuration.hours);
    const minutesNum = Number(effectiveDuration.minutes);
    const durationStr = `${hoursNum}:${String(minutesNum).padStart(2, "0")}`;
    data.append("duration", durationStr);

    // ④ フィンガープリント
    const fingerprint = await getFingerprint();
    data.append("fingerprint", fingerprint);

    // ⑤ Turnstileトークン
    data.append("cf-turnstile-response", token);

    // ⑥ 添付ファイル
    data.append("image", selectedFile);

    // ⑦ API送信
    try {
      await submitPost({ formData: data, mode: "thread" });

      // 成功時の token / session 後処理。
      // 共通: token を破棄 + widget reset (consumeToken)。
      // iOS のみ: スレ立て成功で backend reply session を開始する。startSession
      //   は session が既に走っていれば変えない仕様だが、スレ立てモーダル open
      //   時に必ず clearSession 済みなのでここでは null から now+30min がセット
      //   される。以降の reply は session 経由で token なし送信できる。
      useTurnstileStore.getState().consumeToken();
      if (isIOS) {
        useTurnstileStore.getState().startSession();
      }

      // ⑧ 成功時: フォーム初期化
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData({
          act: "",
          comment: "",
          duration: null,
          allowImageReplies: true,
        });
        handleFileChange(null);
        onSuccess?.();
      }, 200);
    } catch {
      // エラーはuseSubmitPostのonErrorで処理。token は再利用するため保持
    }
  };

  const hintText = formatHint(resolvedLimits);

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      data-turnstile-target-form="thread"
    >
      {showSuccess && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg">
          <div className="w-6 h-6 flex items-center justify-center bg-white rounded-full">
            <FiCheck className="w-4 h-4 text-green-600" />
          </div>
          <span className="font-medium">成功しました！</span>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <Input
            type="text"
            name="act"
            className="w-full"
            value={formData.act}
            onChange={handleChange}
            placeholder="例: sage ID表示"
          />
        </div>

        <div>
          <Textarea
            name="comment"
            value={formData.comment}
            onChange={handleChange}
            placeholder="ｷﾀ━━━━(ﾟ∀ﾟ)━━━━!!"
            rows={6}
          />
        </div>

        <ImageUpload
          file={selectedFile}
          previewUrl={previewUrl}
          onFileChange={handleFileChange}
          variant="button"
        />

        <div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              name="hours"
              value={effectiveDuration.hours}
              onChange={handleDurationChange}
              className="w-16"
              min={0}
              max={resolvedLimits.durationHours}
              step={1}
              inputMode="numeric"
              aria-invalid={durationError ? true : undefined}
              aria-describedby={durationError ? "duration-error" : undefined}
              aria-label="保持期間（時間）"
            />
            <span className="text-sm text-foreground">時間</span>
            <Input
              type="number"
              name="minutes"
              value={effectiveDuration.minutes}
              onChange={handleDurationChange}
              className="w-16"
              min={0}
              max={59}
              step={1}
              inputMode="numeric"
              aria-invalid={durationError ? true : undefined}
              aria-describedby={durationError ? "duration-error" : undefined}
              aria-label="保持期間（分）"
            />
            <span className="text-sm text-foreground">分</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{hintText}</p>
          {durationError && (
            <p id="duration-error" className="text-xs text-destructive mt-1">
              {durationError}
            </p>
          )}
        </div>

        <Checkbox
          checked={formData.allowImageReplies}
          onChange={(checked: boolean): void =>
            setFormData((prev) => ({
              ...prev,
              allowImageReplies: checked,
            }))
          }
          label="画像添付レスを許可"
        />
      </div>

      <Button
        type="submit"
        variant="primary"
        className="w-full"
        disabled={isPending || !selectedFile || Boolean(durationError)}
      >
        {isPending ? "書き込み中..." : "スレッドを立てる"}
      </Button>

      <PostNotice />
    </form>
  );
};
